import { Component, inject, Input, OnInit, signal, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";

import { AdaaHelper, isDsV2 } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { AdaaBoolean } from "../../../constants/enums";
import { EntityModelType, OrgUnit, UserGrantModelType, UserRole, ValueText } from "../../../models";
import { OrgUnitApiService } from "../../../services";
import {
  FormCheckboxComponent,
  FormDropdownComponent,
  FormDropdownMultiComponent,
  FormDropdownTreeComponent,
  FormTwoWaySelectComponent,
} from "../../form";

@Component({
  selector: "adaa-user-grants-modal",
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormCheckboxComponent,
    FormDropdownComponent,
    FormDropdownMultiComponent,
    FormDropdownTreeComponent,
    FormTwoWaySelectComponent,
  ],
  templateUrl: "./user-grants-modal.html",
  styleUrl: "./user-grants-modal.scss",
})
export class UserGrantsModalComponent implements OnInit {
  private _translateService = inject(TranslateService);
  private _formBuilder = inject(FormBuilder);
  private _orgUnitApiService = inject(OrgUnitApiService);
  modal = inject(NgbActiveModal);

  orgUnitsGrantsModal: OrgUnit[] = [];

  @Input() entities: ValueText[] = [];
  @Input() userGrants: UserGrantModelType[] = [];
  @Input() idProp: string;

  @Input() entitiesCopy: EntityModelType[] = [];
  @Input() rolesList: UserRole[] = [];
  @Input() userId: number | undefined = undefined;

  private _grantsSelect = viewChild.required<FormTwoWaySelectComponent>("grantsSelect");

  grantsForm: FormGroup;
  AdaaHelper = AdaaHelper;
  readonly isDsV2 = () => isDsV2();

  orgUnitsOptions = signal<ValueText[]>([]);
  grantsOptions = signal<ValueText[]>([]);
  advancedGrantsOptions = signal<ValueText[]>([]);

  private get _currentEntity(): number | undefined {
    return AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentEntity, {
      type: "prop",
      property: "id",
    }) as number | undefined;
  }

  public ngOnInit() {
    this.grantsForm = this._prepareForm();

    const entityId = this.grantsForm.get("entityId")?.value;
    if (entityId) {
      this._orgUnitApiService.getAllByEntity(entityId).subscribe({
        next: (response) => {
          if (!response.inError) {
            this.orgUnitsGrantsModal = response.responseData || [];
            this._computeOrgUnitsOptions();
          }
        },
      });
    } else {
      this._computeOrgUnitsOptions();
    }

    this._computeGrantsOptions();
    this.setGrantsDdlValue();
  }

  public entityIdChanged(value: number) {
    if (AdaaHelper.isDefinedAndNotEmpty(value)) {
      this._orgUnitApiService.getAllByEntity(value).subscribe({
        next: (response) => {
          if (response.inError) return;
          this.orgUnitsGrantsModal = response.responseData || [];
          this._computeOrgUnitsOptions();

          this._computeGrantsOptions();
          this.setGrantsDdlValue();
          this._grantsSelect().doSearch();
        },
      });
    } else {
      this.orgUnitsGrantsModal = [];
      this._computeOrgUnitsOptions();

      this._computeGrantsOptions();
      this.setGrantsDdlValue();
      this._grantsSelect().doSearch();
    }
  }

  public orgUnitIdChanged() {
    this.setGrantsDdlValue();
    this._grantsSelect().doSearch();
  }

  public grantsChanged(values: number[]) {
    if (!values) return;
    const entityId = this.grantsForm.get("entityId")!.value as number;
    const orgUnitId = this.grantsForm.get("orgUnitId")!.value as number;

    const entityObject = this.entitiesCopy.find((e) => e.id === entityId);
    const orgUnitObject = this.orgUnitsGrantsModal.find((e) => e.id === orgUnitId);

    values.forEach((roleId) => {
      const grant = this.userGrants.find(
        (e) => e.roleId === roleId && e.entityId == entityId && e.orgUnitId == orgUnitId
      );

      const grantObject = this.rolesList.find((e) => e.id === roleId);

      if (!grant)
        this.userGrants.push({
          id: undefined,
          fakeId: Math.floor(Math.random() * 217 * 1000),
          roleId: roleId,
          roleNameEN: grantObject ? grantObject.nameEN : "",
          roleNameAE: grantObject ? grantObject.nameAE : "",
          status: Constants.OBJECT_STATUS.ACTIVE,
          userId: this.userId,
          entityId: entityId,
          entityNameEN: entityObject ? entityObject.nameEN : "",
          entityNameAE: entityObject ? entityObject.nameAE : "",
          orgUnitId: orgUnitId,
          orgUnitNameEN: orgUnitObject ? orgUnitObject.nameEN : "",
          orgUnitNameAE: orgUnitObject ? orgUnitObject.nameAE : "",
          orgUnitPlanId: orgUnitObject && orgUnitObject.planId !== 0 ? orgUnitObject?.planId : undefined,
          orgUnitPlanNameEN: orgUnitObject ? orgUnitObject.planNameEN : "",
          orgUnitPlanNameAE: orgUnitObject ? orgUnitObject.planNameAE : "",
          updateUser: undefined,
          updateTS: undefined,
        });
    });

    //Selected entity and orgUnit
    let currentGrants = this.userGrants.filter((e) => e.entityId == entityId && e.orgUnitId == orgUnitId);

    //Other Grants
    const grants = this.userGrants.filter((e) => e.entityId != entityId || e.orgUnitId != orgUnitId);

    currentGrants = currentGrants.filter((e) => values.includes(e.roleId));

    this.userGrants = [...currentGrants, ...grants];

    this.setGrantsDdlValue();
  }

  public advancedGrantsChanged() {
    this._computeAdvancedGrantsOptions();

    const entities = this.grantsForm.get("entities")!.value as number[];
    const grants = this.grantsForm.get("grantsAdvanced")!.value as number[];

    if (!grants || !entities) return;

    entities.forEach((entity) => {
      grants.forEach((roleId) => {
        const entityObject = this.entitiesCopy.find((e) => e.id === entity);
        const grant = this.userGrants.find(
          (e) => e.roleId === roleId && e.entityId == entity && !AdaaHelper.isDefinedAndNotEmpty(e.orgUnitId)
        );

        const grantObject = this.rolesList.find((e) => e.id === roleId);

        if (
          (grantObject?.allowExternal === AdaaBoolean.N &&
            entityObject?.entityTypeId === Constants.ENTITY_TYPES.EXTERNAL) ||
          (grantObject?.allowParticipating === AdaaBoolean.N &&
            entityObject?.entityTypeId === Constants.ENTITY_TYPES.PARTICIPATING)
        )
          return;

        if (!grant)
          this.userGrants.push({
            id: undefined,
            fakeId: Math.floor(Math.random() * 217 * 1000),
            roleId: roleId,
            roleNameEN: grantObject ? grantObject.nameEN : "",
            roleNameAE: grantObject ? grantObject.nameAE : "",
            status: Constants.OBJECT_STATUS.ACTIVE,
            userId: this.userId,
            entityId: entity,
            entityNameEN: entityObject ? entityObject.nameEN : "",
            entityNameAE: entityObject ? entityObject.nameAE : "",
            orgUnitId: undefined,
            orgUnitNameEN: "",
            orgUnitNameAE: "",
            orgUnitPlanId: undefined,
            orgUnitPlanNameEN: "",
            orgUnitPlanNameAE: "",
            updateUser: undefined,
            updateTS: undefined,
          });
      });
    });

    this._computeAdvancedGrantsOptions();
  }

  public save() {
    this.modal.close(this.userGrants);
  }

  public setGrantsDdlValue() {
    const entityId = this.grantsForm.get("entityId")?.value;
    const orgUnitId = this.grantsForm.get("orgUnitId")?.value;

    const grants = this.userGrants
      .filter(
        (e) =>
          (entityId ? e.entityId === +entityId : e.entityId == null) &&
          (orgUnitId ? e.orgUnitId === +orgUnitId : e.orgUnitId == null)
      )
      .map((e) => e.roleId);

    //Sometimes the grants value will be null this to avoid that
    setTimeout(() => {
      this.grantsForm.get("grants")?.setValue(grants);
    }, 0);
  }

  public resetForm() {
    this.grantsForm.get("entityId")?.setValue(AdaaHelper.isPMOEntity() ? null : this._currentEntity);
    this.grantsForm.get("orgUnitId")?.setValue(null);
    this.grantsForm.get("grants")?.setValue(null);
    this.grantsForm.get("entities")?.setValue(null);
    this.grantsForm.get("grantsAdvanced")?.setValue(null);
    this._computeGrantsOptions();
    this._computeAdvancedGrantsOptions();
  }

  private _computeOrgUnitsOptions() {
    const orgUnits = AdaaHelper.setDropdownArray(
      this.orgUnitsGrantsModal,
      "id",
      AdaaHelper.getFieldLanguage("name"),
      "parentOrgUnit"
    );
    orgUnits.unshift({ value: null, text: this._translateService.instant("common.form.label.none") });
    this.orgUnitsOptions.set(orgUnits);
  }

  private _computeGrantsOptions() {
    const entityId = AdaaHelper.isPMOEntity() ? this.grantsForm?.get("entityId")?.value : this._currentEntity;

    if (entityId) {
      const entity = this.entitiesCopy.find((e) => e.id === entityId);

      let options = this.rolesList;

      if (entity?.entityTypeId === Constants.ENTITY_TYPES.EXTERNAL) {
        options = options.filter((e) => e.allowExternal === AdaaBoolean.Y);
      } else if (entity?.entityTypeId === Constants.ENTITY_TYPES.PARTICIPATING) {
        options = options.filter((e) => e.allowParticipating === AdaaBoolean.Y);
      }

      this.grantsOptions.set(AdaaHelper.setDropdownArray(options, "id", AdaaHelper.getFieldLanguage("name")));
    } else {
      this.grantsOptions.set([]);
    }
  }

  private _computeAdvancedGrantsOptions() {
    const entities = this.grantsForm.get("entities")?.value;
    if (entities?.length > 0) {
      this.advancedGrantsOptions.set(
        AdaaHelper.setDropdownArray(this.rolesList, "id", AdaaHelper.getFieldLanguage("name"))
      );
    } else {
      this.advancedGrantsOptions.set([]);
    }
  }

  private _prepareForm() {
    return this._formBuilder.group({
      entityId: AdaaHelper.isPMOEntity() ? null : this._currentEntity,
      orgUnitId: null,
      grants: null,
      isAdvancedMode: false,
      entities: null,
      grantsAdvanced: null,
    });
  }
}
